import { Checkbox, Col, Form, Input, InputNumber, Modal, Row, Select } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { useTranslation } from "react-i18next";
import { getUserDataById } from "../../../../../functions/users";
import { clone } from "lodash";
import dayjs from "dayjs";
import { FIRESTORE_CUSTOMERS_TABLE } from "../../../../../constants/FirestoreConstant";
import { doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { updateExactAccount } from "../../../../../functions/api/exact";
import { showOperationSummary } from "../../../../../utils/operationSummary";
import { CHEFS_FARM, GROWY } from "../../../../../constants/CompanyConstants";
import { MARKETING_CODES } from "../../../../../constants/ProductConstant";
import { formatPhoneNumber } from "../../../../../functions/formatters";
import TextArea from "antd/es/input/TextArea";
import { InfoCircleOutlined } from "@ant-design/icons";

export default function EditCompanyDetailsModal({ company, visible, onClose, exactCustomers, webshopUsers, cmsUsers }) {
  const { t } = useTranslation();
  const [companyDetailsForm] = Form.useForm();
  const { email } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [companyNameSameAsRestaurant, setCompanyNameSameAsRestaurant] = useState(true);

  const isGrowyCustomer = useMemo(() => {
    return company?.company === GROWY;
  }, [company]);

  const isChefsFarmCustomer = useMemo(() => {
    return company?.company === CHEFS_FARM || !company?.company;
  }, [company]);

  useEffect(() => {
    if (company && visible) {
      // Check if companyName differs from customerName
      const hasCompanyName = company.companyName && company.companyName !== company.customerName;
      setCompanyNameSameAsRestaurant(!hasCompanyName);

      companyDetailsForm.setFieldsValue({
        exactId: company.exactId,
        exactAccountCode: company.exactAccountCode,
        customerName: company.customerName,
        companyName: company.companyName || "",
        customerEmail: company.customerEmail,
        customerPhone: company.customerPhone,
        contactPerson: company.customerUsers[0],
        internalNotes: !!company.internalNotes ? company.internalNotes.text : null,
        kvkNumber: company.kvkNumber,
        taxNumber: company.taxNumber,
        customerGln: company.customerGln,
        customerDeliveryGln: company.customerDeliveryGln,
        company: company.company,
        marketingCode: company.marketingCode || null,
      });
      if (company.defaultReference) {
        companyDetailsForm.setFieldsValue({
          defaultReference: company.defaultReference,
        });
      }
    } else {
      companyDetailsForm.resetFields();
      setCompanyNameSameAsRestaurant(true);
    }
  }, [company, visible]);

  const handleSaveCompanyDetails = () => {
    companyDetailsForm.validateFields().then(async (values) => {
      setIsLoading(true);

      // Initialize operation results
      const operationResults = {
        firestore: { attempted: true, success: false, error: null },
        exact: { attempted: false, success: false, error: null },
      };

      const obj = {
        exactId: values.exactId || null,
        exactAccountCode: values.exactAccountCode || null,
        customerName: values.customerName,
        customerEmail: values.customerEmail,
        customerPhone: values.customerPhone ? formatPhoneNumber(values.customerPhone) : null,
        kvkNumber: Number(values.kvkNumber) || 0,
        taxNumber: values.taxNumber || null,
        customerGln: values.customerGln || null,
        customerDeliveryGln: values.customerDeliveryGln || null,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      };

      // Handle companyName based on checkbox state
      if (companyNameSameAsRestaurant) {
        // If checkbox is checked, don't save companyName (or explicitly set to null to remove it)
        obj.companyName = null;
      } else {
        // If checkbox is unchecked, save the companyName
        obj.companyName = values.companyName?.trim() || null;
      }

      if (values.internalNotes) {
        // Check for existing internal notes and update if the new notes differ from the old ones
        if (company.internalNotes) {
          if (company.internalNotes.text !== values.internalNotes) {
            obj.internalNotes = {
              ...company.internalNotes,
              text: values.internalNotes,
              updatedAt: dayjs().unix(),
              updatedBy: email,
            };
          } else {
            obj.internalNotes = company.internalNotes;
          }
        } else {
          // Otherwise, create a new internalNotes object
          obj.internalNotes = { text: values.internalNotes, createdAt: dayjs().unix(), createdBy: email };
        }
      }

      if (values.exactId && !!exactCustomers) {
        const exactCustomer = exactCustomers.find((item) => item.ID === values.exactId);
        if (!!exactCustomer && exactCustomer.AccountCode !== values.exactAccountCode) {
          obj.exactAccountCode = exactCustomer.AccountCode.trim();
        }
      }
      // Check if contact person changed
      if (
        !!company.customerUsers.length &&
        !!values.contactPerson &&
        company.customerUsers[0] !== values.contactPerson
      ) {
        const clonedCustomerUsers = clone(company.customerUsers);
        const contactPersonIndex = clonedCustomerUsers.findIndex((item) => item === values.contactPerson);
        if (contactPersonIndex > -1) {
          const itemAtIndex = clonedCustomerUsers.splice(contactPersonIndex, 1)[0];
          clonedCustomerUsers.unshift(itemAtIndex);
        }
        obj.customerUsers = clonedCustomerUsers;
      }

      obj.marketingCode = values.marketingCode || null;

      obj.defaultReference = values.defaultReference || null;

      // Update Firestore first
      try {
        await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, company.id), obj);

        operationResults.firestore.success = true;

        // If customer has exactId, also update Exact
        if (company.exactId) {
          operationResults.exact.attempted = true;
          try {
            const token = await getFirebaseIDToken();
            const exactUpdateObj = {
              customerName: values.customerName,
              exactAccountId: company.exactId,
              customerEmail: values.customerEmail,
              customerPhone: values.customerPhone,
              kvkNumber: values.kvkNumber,
              taxNumber: values.taxNumber,
              marketingCode: values.marketingCode,
            };

            await updateExactAccount(exactUpdateObj, token);
            operationResults.exact.success = true;
          } catch (exactError) {
            console.error("Exact update error:", exactError);
            // Extract the most detailed error message available
            const detailedError =
              exactError.response?.data?.Message || exactError.response?.data?.message || exactError.message;
            operationResults.exact.error = detailedError;
          }
        }

        setIsLoading(false);
        onClose();

        showOperationSummary(operationResults, t);
      } catch (firestoreError) {
        console.error("Firestore error:", firestoreError);
        operationResults.firestore.error = firestoreError.message;
        setIsLoading(false);
        showOperationSummary(operationResults, t);
      }
    });
  };

  return (
    <Modal
      title={t("edit_company_details_value", { company: !!company ? company.customerName : "" })}
      open={visible}
      onOk={handleSaveCompanyDetails}
      onCancel={onClose}
      okText={t("save")}
      cancelText={t("close")}
      okButtonProps={{ loading: isLoading, htmlType: "submit" }}
      zIndex={1002}
      centered
      width={800}
    >
      <Form form={companyDetailsForm} layout="vertical">
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              name={"customerName"}
              label={isChefsFarmCustomer ? t("restaurant_name") : t("customer_name")}
              rules={[
                {
                  required: true,
                  message: isChefsFarmCustomer ? t("form.enter_restaurant_name") : t("form.enter_customer_name"),
                },
              ]}
            >
              <Input placeholder={isChefsFarmCustomer ? t("restaurant_name") : t("customer_name")} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col xs={24} style={{ marginBottom: 12 }}>
            <Checkbox
              checked={companyNameSameAsRestaurant}
              onChange={(e) => setCompanyNameSameAsRestaurant(e.target.checked)}
            >
              {isChefsFarmCustomer ? t("company_name_same_as_restaurant") : t("company_name_same_as_customer")}
            </Checkbox>
          </Col>
        </Row>
        {!companyNameSameAsRestaurant && (
          <Row>
            <Col xs={24}>
              <Form.Item
                name={"companyName"}
                label={t("company_name")}
                rules={[{ required: true, message: t("form.enter_company_name") }]}
              >
                <Input placeholder={t("company_name")} />
              </Form.Item>
            </Col>
          </Row>
        )}
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item
              name={"customerEmail"}
              label={t("email")}
              rules={[{ required: true, message: t("form.enter_email") }]}
            >
              <Input placeholder={t("email")} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name={"customerPhone"}
              label={t("phone")}
              rules={[
                {
                  pattern: /^[+]?(?:[0-9][\s\-]*){10,15}$/,
                  message: t("form.enter_valid_phone"),
                },
                {
                  type: "tel",
                },
              ]}
              tooltip={{ title: t("enter_phone_tooltip"), icon: <InfoCircleOutlined /> }}
            >
              <Input placeholder={t("phone")} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name={"kvkNumber"} label={t("kvk_number")} initialValue={0}>
              <InputNumber placeholder={t("kvk_number")} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name={"taxNumber"} label={t("vat_number")}>
              <Input placeholder={t("vat_number")} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name={"customerGln"}
              label={t("gln_number")}
              rules={[
                {
                  pattern: /^[0-9]{13}$/,
                  message: t("form.enter_valid_gln"),
                },
              ]}
            >
              <Input placeholder={t("gln_number")} maxLength={13} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name={"customerDeliveryGln"}
              label={t("gln_delivery_number")}
              tooltip={{ title: t("form.gln_delivery_number_tooltip") }}
              rules={[
                {
                  pattern: /^[0-9]{13}$/,
                  message: t("form.enter_valid_gln"),
                },
              ]}
            >
              <Input placeholder={t("gln_delivery_number")} maxLength={13} />
            </Form.Item>
          </Col>

          <Col xs={12}>
            <Form.Item name="marketingCode" label={t("customer.type")}>
              <Select placeholder={t("choose_a_customer_type")}>
                {MARKETING_CODES.map((code) => (
                  <Select.Option key={code.value} value={code.value}>
                    {code.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item name="defaultReference" label={t("default_reference")}>
              <Input placeholder={t("default_reference")} maxLength={20} />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item name={"exactId"} label={t("exact_customer_id")}>
              <Select
                style={{ width: "100%" }}
                placeholder={t("exact_customer_id")}
                showSearch
                optionFilterProp="children"
              >
                {exactCustomers?.map((item) => (
                  <Select.Option key={item.ID} value={item.ID}>
                    {`${item.AccountName} (${item.AccountCode.trim()})`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {!!company && company.customerUsers.length > 0 && (
            <Col xs={24}>
              <Form.Item name={"contactPerson"} label={t("contact_person")}>
                <Select style={{ width: "100%" }} placeholder={t("contact_person")}>
                  {company.customerUsers.map((user) => {
                    const userData = getUserDataById(webshopUsers, cmsUsers, user);
                    if (userData) {
                      return (
                        <Select.Option key={user} value={user} disabled={!userData.isActive}>
                          {userData.user}
                        </Select.Option>
                      );
                    }
                  })}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row>
          <Col xs={24}>
            <Form.Item name={"internalNotes"} label={t("internal_notes")}>
              <TextArea placeholder={t("internal_customer_notes_placeholder")} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
